import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, RefreshCw } from 'lucide-react';
import type { ClientProfile, DeskAttachmentInput } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { AttachmentPicker } from '../desk/AttachmentPicker';
import { CLIENT_DESK } from '../desk/desk-api';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function RenewalPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/client/renewals');
  const initialTenantId = params.get('tenantId') ?? '';
  const [tenantId, setTenantId] = useState(initialTenantId);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<DeskAttachmentInput[]>([]);
  const [error, setError] = useState<string | null>(null);

  const profile = useQuery({
    queryKey: ['client-profile'],
    queryFn: () => get<ClientProfile>('/client/me'),
  });
  const labs = profile.data?.labs ?? [];

  const amountMinor = Math.round(Number(amount) * 100);
  const valid = Boolean(tenantId) && Number.isFinite(amountMinor) && amountMinor > 0 && files.length > 0;

  const send = useMutation({
    mutationFn: () =>
      post('/client/renewals', {
        tenantId,
        amountMinor,
        reference: reference.trim() || undefined,
        note: note.trim() || undefined,
        attachments: files,
      }),
    onSuccess: () => {
      toast.success('Request sent', 'The owner will confirm the payment and set the new date.');
      void queryClient.invalidateQueries({ queryKey: ['client-renewals'] });
      void queryClient.invalidateQueries({ queryKey: ['client-profile'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !send.isSuccess && (tenantId !== initialTenantId || amount !== '' || reference !== '' || note !== '' || files.length > 0);

  return (
    <FormScreen
      title="Request a renewal"
      description="Attach the transfer screenshot or deposit slip."
      icon={RefreshCw}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        send.mutate();
      }}
    >
      <FormSection title={tr('billing', 'payment')} icon={RefreshCw}>
        <Field label="Hospital" required className="sm:col-span-2">
          <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">Select a hospital…</option>
            {labs.map((lab) => (
              <option key={lab.tenantId} value={lab.tenantId}>
                {lab.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Amount paid" required>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} />
        </Field>
        <Field label="Transfer reference" hint="Whatever the bank slip quotes">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Note" className="sm:col-span-2">
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'receipt')} icon={Paperclip} columns={1}>
        <Field label="Proof of payment" required>
          <AttachmentPicker endpoints={CLIENT_DESK} extra={tenantId ? { tenantId } : undefined} value={files} onChange={setFiles} disabled={!tenantId} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Send request" loading={send.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
